import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { appVersionService, UpdateCheckResult } from '@/services/appVersionService';
import { createScopedLogger } from '@/utils/logging/createScopedLogger';

const logger = createScopedLogger('AppVersionContext');
const logWithTimestamp = (msg: string, ...args: unknown[]) => logger.info(msg, ...args);
const errorWithTimestamp = (msg: string, ...args: unknown[]) => logger.error(msg, ...args);

const isWeb = Platform.OS === 'web';

interface AppVersionState {
  forceUpdateRequired: boolean;
  recommendedUpdateAvailable: boolean;
  updateConfig: UpdateCheckResult | null;
  isChecking: boolean;
  checkForUpdates: (bypassCache?: boolean, isStartupCheck?: boolean) => Promise<void>;
  dismissRecommendedUpdate: () => Promise<void>;
}

const defaultState: AppVersionState = {
  forceUpdateRequired: false,
  recommendedUpdateAvailable: false,
  updateConfig: null,
  isChecking: false,
  checkForUpdates: async () => {},
  dismissRecommendedUpdate: async () => {},
};

const AppVersionContext = createContext<AppVersionState>(defaultState);

interface AppVersionProviderProps {
  children: ReactNode;
}

export const AppVersionProvider: React.FC<AppVersionProviderProps> = ({ children }) => {
  const [forceUpdateRequired, setForceUpdateRequired] = useState(false);
  const [recommendedUpdateAvailable, setRecommendedUpdateAvailable] = useState(false);
  const [updateConfig, setUpdateConfig] = useState<UpdateCheckResult | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const appState = useRef(AppState.currentState);
  const hasInitialized = useRef(false);

  const checkForUpdates = useCallback(async (bypassCache = false, isStartupCheck = false) => {
    if (isWeb) return;

    if (isChecking && !isStartupCheck) {
      return;
    }

    setIsChecking(true);

    try {
      const result = await appVersionService.checkForUpdates(bypassCache, isStartupCheck);

      if (result) {
        setUpdateConfig(result);
        setForceUpdateRequired(result.forceUpdateRequired);
        setRecommendedUpdateAvailable(result.recommendedUpdateAvailable);

        if (result.forceUpdateRequired) {
          logWithTimestamp('[AppVersionContext] FORCE UPDATE REQUIRED');
        }
      } else if (isStartupCheck) {
        setForceUpdateRequired(false);
        setRecommendedUpdateAvailable(false);
      }
    } catch (error) {
      errorWithTimestamp('[AppVersionContext] Error checking for updates:', error);
    } finally {
      setIsChecking(false);
    }
  }, [isChecking]);

  const dismissRecommendedUpdate = useCallback(async () => {
    if (isWeb) return;

    if (!updateConfig?.recommendedVersion) {
      return;
    }

    await appVersionService.dismissRecommendedUpdate(updateConfig.recommendedVersion);
    setRecommendedUpdateAvailable(false);
  }, [updateConfig?.recommendedVersion]);

  useEffect(() => {
    if (isWeb) return;

    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    const delay = 1000;
    const timer = setTimeout(() => {
      logWithTimestamp('[AppVersionContext] Performing STARTUP check');
      checkForUpdates(false, true);
    }, delay);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isWeb) return;

    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        logWithTimestamp('[AppVersionContext] App came to foreground, checking for updates...');
        checkForUpdates(true);
      }

      appState.current = nextAppState;
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);

    return () => {
      subscription.remove();
    };
  }, [checkForUpdates]);

  const value: AppVersionState = {
    forceUpdateRequired,
    recommendedUpdateAvailable,
    updateConfig,
    isChecking,
    checkForUpdates,
    dismissRecommendedUpdate,
  };

  return (
    <AppVersionContext.Provider value={value}>
      {children}
    </AppVersionContext.Provider>
  );
};

export const useAppVersionContext = (): AppVersionState => {
  return useContext(AppVersionContext);
};
